import { Col, Row } from 'antd';
import * as _ from 'lodash';
import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  createContext,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { ReactComponent as CloseIcon } from 'app/assets/img/bold_close_icon.svg';
import ContainerFluid from 'app/components/Layout/ContainerFluid';
import { LoadingContainer } from 'app/components/LoadingContainer';
import ModalContainer from 'app/components/ModalContainer';
import PromotionDetail from 'app/components/PromotionDetail';
import useWindowSize from 'app/hooks/useWindowSize';
import { RootState } from 'types';
import {
  BP_TYPE,
  DELIVER_TICKET_TYPE_CODE,
  TICKET_TYPES,
} from 'utils/constants';
import { useGlobalFunction } from '../App/hooks/useGlobalFunction';
import { selectInfoUser, selectTranslations } from '../App/slice/selectors';
import { selectListDeliverTicketType } from '../Checkout/slice/selectors';
import PaymentInfo from './components/PaymentInfo';
import { CART_ERROR_TYPE } from './components/TicketItem';
import TicketList from './components/TicketList';
import {
  I_TicketDataToGetPrice,
  OnGetTicketPrice,
  OnMergeTicket,
  OnRemoveTicket,
  OnRemoveTicketList,
  OnSetCart,
  OnUpdateTicket,
  PaymentMethod,
  useCartNew,
} from './hooks/useCartNew';
import { I_CartItem, Park } from './slice/types';
import { generateTicketIdentify } from './utils';

interface Props {}

export interface I_TicketItemByPark {
  park: Park;
  tickets: I_CartItem[];
}

type FormCustomContextType = {
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  isSubmitted: boolean;
  setIsSubmitted: Dispatch<SetStateAction<boolean>>;
  onUpdateTicket: OnUpdateTicket;
  onRemoveTicket: OnRemoveTicket;
  onMergeTicket: OnMergeTicket;
  onGetTicketPrice: OnGetTicketPrice;
  onGetCartPrice: (cartData?: I_CartItem[]) => void;
  paymentMethod: PaymentMethod;
  setPaymentMethod: Dispatch<SetStateAction<PaymentMethod>>;
  onChangePaymentMethod: (value: PaymentMethod) => void;
  deliverTicketType: null | number;
  setDeliverTicketType: Dispatch<SetStateAction<number | null>>;
  listCart: I_CartItem[];
  ticketsSortedByPark: I_TicketItemByPark[];
  onSetCart: OnSetCart;
  totalAmountSelected: number;
  totalPriceSelected: number;
  onGoToCatologPage: () => void;
  onRemoveTicketList: OnRemoveTicketList;
  ticketsMergedNeedUpdateAmount: MutableRefObject<string[]>;
  allocatedDebt: number | null;
  partner?: any;
};

export const FormCustomContext = createContext<FormCustomContextType>(
  {} as FormCustomContextType,
);

export const Cart = memo((props: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [promotionData, setPromotionData] = useState<any>(null);
  const { device } = useWindowSize();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);
  const [allocatedDebt] = useState<number | null>(null);
  const [deliverTicketType, setDeliverTicketType] = useState<number | null>(
    null,
  );
  const ticketsMergedNeedUpdateAmount = useRef<string[]>([]);
  const [partner] = useState<any>(null);

  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const listDeliverTicketType = useSelector(selectListDeliverTicketType);
  const translations = useSelector(selectTranslations);

  const [deliverTicketTypeList, setDeliverTicketTypeList] = useState<
    Record<string, any>[]
  >([]);
  const [valueTicket, setValueTicket] = useState<number>(0);
  const [disableCreateOrder, setDisableCreateOrder] = useState(false);

  const userInfo = useSelector(selectInfoUser);
  const isProxyBuyer =
    (userInfo?.businessPartner as any)?.bpType === BP_TYPE.PROXY_BUYER;

  const isLoadingCartData = useSelector(
    (state: RootState) => state.cart?.loading,
  );

  const {
    ticketsSortedByPark,
    listCart,
    onSetCart,
    onUpdateTicket,
    onRemoveTicket,
    totalAmountSelected,
    totalPriceSelected,
    onGoToCatologPage,
    onMergeTicket,
    onGetTicketPrice,
    onGetCart,
    onRemoveTicketList,
  } = useCartNew();

  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const isAbleGetCartPrice = useRef<boolean>(true);
  const isLoadedCartData = useRef<boolean>(false);

  const onChangePaymentMethod = () => {
    isAbleGetCartPrice.current = true;
  };

  useEffect(() => {
    if (!listDeliverTicketType?.length) return;

    const canBuyPaperTicket: boolean = (() => {
      if (allocatedDebt) return partner?.allowBuyPaperTicket;

      return userInfo?.businessPartner?.allowBuyPaperTicket;
    })();

    const _deliverTicketTypeList = canBuyPaperTicket
      ? listDeliverTicketType
      : listDeliverTicketType.filter(
          i => i?.code !== DELIVER_TICKET_TYPE_CODE.PAPER_TICKET,
        );

    if (!_deliverTicketTypeList?.some(i => i?.id === deliverTicketType)) {
      const currentDeliverTicketType = listDeliverTicketType?.find(
        i => i?.id === deliverTicketType,
      );

      const isPaperTicket =
        currentDeliverTicketType?.code ===
        DELIVER_TICKET_TYPE_CODE.PAPER_TICKET;

      if (isPaperTicket) {
        onOpenDialogNoti({
          type: 'ERROR',
          isShow: true,
          title: translations?.['b2b.txt.noApplyPaperTicket'] ?? '',
          content: '',
          maskClosable: false,
          onCloseDialog: onCloseDialogNoti,
        });
      }

      const findEVoucher = _deliverTicketTypeList.find(
        (item: any) => _.isEmpty(item?.code) || item?.id === 27964,
      );

      setDeliverTicketType(
        !!findEVoucher ? findEVoucher.id : _deliverTicketTypeList?.[0]?.id,
      );
    }

    setDeliverTicketTypeList(_deliverTicketTypeList);
  }, [
    allocatedDebt,
    deliverTicketType,
    isProxyBuyer,
    listDeliverTicketType,
    onCloseDialogNoti,
    onOpenDialogNoti,
    partner?.allowBuyPaperTicket,
    partner?.id,
    translations,
    userInfo?.businessPartner?.allowBuyPaperTicket,
  ]);

  useEffect(() => {
    if (!isLoadingCartData) setIsFirstLoad(false);
  }, [isLoadingCartData]);

  const onGetCartPrice = useCallback((cartData?: I_CartItem[]) => {
    const onSuccess = (response, currentCartData) => {
      const responseAsObject: Record<string, Record<string, any>> = {};

      response?.forEach(item => {
        const identify = generateTicketIdentify({
          showId: item?.show_id,
          performanceId: undefined,
          zoneId: item?.zone_id,
          date: item?.date,
          restaurantId: item?.restaurant?.id ?? item?.restaurant_id,
          usedArea2: item?.used_area2,
        });
        responseAsObject[identify] = item;
      });

      onSetCart(
        currentCartData?.map(ticket => {
          const ticketResponseData = responseAsObject?.[ticket?.identify];
          if (ticketResponseData) {
            const newInventory = +(
              (isProxyBuyer ? Infinity : ticketResponseData?.inventory) ?? 0
            );
            const err = ticketResponseData?.error;

            return {
              ...ticket,
              inventory: newInventory,
              price: ticketResponseData?.price,
              originalPrice: ticketResponseData?.originalPrice,
              selected: !!+newInventory ? ticket?.selected : false,
              errorTime:
                ticketResponseData?.errorTime ??
                (err?.errorType === CART_ERROR_TYPE.ERROR_TIME
                  ? err?.message
                  : ''),
              errorTicket:
                ticketResponseData?.errorTicket ??
                ([
                  CART_ERROR_TYPE.ERROR_TICKET,
                  CART_ERROR_TYPE.JUMP_TICKET,
                  CART_ERROR_TYPE.MIN_TICKET,
                ].some(item => item === err?.errorType)
                  ? err?.message
                  : ''),
            };
          }
          return { ...ticket };
        }),
      );
    };

    const onError = () => {};

    // Sau khi merge ticket, state listCart trong render hiện tại có thể chưa kịp cập nhật.
    // Cho phép truyền snapshot cart mới để re-check giá toàn bộ giỏ đúng theo dữ liệu vừa merge.
    const currentCart = cartData ?? listCart;

    const listCartToGetPrice = currentCart.filter(i => {
      const isNotApplyZone =
        i?.show?.zones?.length === 0 ||
        i?.show?.materialType === TICKET_TYPES.CABLE_CAR;
      const isNotApplyUsedArea2 =
        i?.show?.materialType === TICKET_TYPES.CABLE_CAR;
      const isRestaurantTicket =
        i?.show?.restaurants?.length > 0 &&
        i?.show?.materialType !== TICKET_TYPES.CABLE_CAR;

      if (
        (!isNotApplyUsedArea2 && !i?.usedArea2) ||
        (!isNotApplyZone && !i?.zone) ||
        (isRestaurantTicket && !i?.restaurant?.id)
      )
        return false;

      return true;
    });

    const dataToGetPrice: I_TicketDataToGetPrice[] = listCartToGetPrice?.map(
      i => ({
        show_id: i?.show?.id,
        // performance: i?.performance?.id,
        used_area2: i?.usedArea2,
        date: i?.date,
        zone_id: i?.zone?.id,
        amount: i?.amount?.toString() ?? '',
        restaurant_id: i?.restaurant?.id,
      }),
    );

    onGetTicketPrice(
      dataToGetPrice,
      paymentMethod?.toString() ?? '',
      onSuccess,
      onError,
      allocatedDebt,
    );
  }, [
    onGetTicketPrice,
    onSetCart,
    paymentMethod,
    allocatedDebt,
    isProxyBuyer,
    listCart,
  ]);

  useEffect(() => {
    const onGetCartPriceAtFirstMount = () => {
      if (
        !isAbleGetCartPrice.current ||
        listCart?.length < 1 ||
        !paymentMethod ||
        !isLoadedCartData.current
      )
        return false;
      onGetCartPrice();
      isAbleGetCartPrice.current = false;
    };
    onGetCartPriceAtFirstMount();
  }, [listCart, onGetCartPrice, onGetTicketPrice, paymentMethod]);

  const isShowTag = useMemo(() => {
    if (!!partner) {
      return !!partner?.bpLongTerm && !!partner?.allowBuyShortTerm;
    }

    return (
      !!userInfo?.businessPartner?.bpLongTerm &&
      !!userInfo?.businessPartner?.allowBuyShortTerm
    );
  }, [userInfo, partner]);

  useEffect(() => {
    if (!!userInfo) {
      onGetCart(
        true,
        () => {
          isLoadedCartData.current = true;
        },
        isShowTag
          ? {
              ticketType: valueTicket,
              ...(!!partner && {
                bpAllocatedDebt: partner?.id,
              }),
            }
          : {
              ticketType: !!userInfo?.businessPartner?.bpLongTerm ? '1' : '0',
              ...(!!partner && {
                bpAllocatedDebt: partner?.id,
              }),
            },
      );
    }
  }, [onGetCart, valueTicket, partner, isShowTag, userInfo]);

  const onChangeTicket = (value?: number) => {
    isAbleGetCartPrice.current = true;
    if (typeof value === 'number') {
      sessionStorage.setItem('ticketType', `${value}`);
      setValueTicket(value);
    } else {
      sessionStorage.setItem('ticketType', `${valueTicket === 1 ? 0 : 1}`);
      setValueTicket(prev => {
        return prev === 1 ? 0 : 1;
      });
    }
  };

  const isCreatingOrderInProxyBuyerFlow = useRef(false);

  return (
    <>
      <FormCustomContext.Provider
        value={{
          isLoading,
          setIsLoading,
          isSubmitted,
          setIsSubmitted,
          listCart,
          ticketsSortedByPark,
          onSetCart,
          totalAmountSelected,
          totalPriceSelected,
          onGoToCatologPage,
          onUpdateTicket,
          onRemoveTicket,
          onMergeTicket,
          onGetCartPrice,
          paymentMethod,
          setPaymentMethod,
          onGetTicketPrice,
          onChangePaymentMethod,
          deliverTicketType,
          setDeliverTicketType,
          onRemoveTicketList,
          ticketsMergedNeedUpdateAmount,
          allocatedDebt,
          partner,
        }}
      >
        <LoadingContainer loading={isLoading || !isLoadedCartData.current}>
          <ContainerFluid>
            <Col span={24}>
              {isLoadedCartData.current ? (
                <Row gutter={26}>
                  <Col span={device === 'desktop' ? 16 : 24}>
                    <TicketList
                      disableCreateOrder={disableCreateOrder}
                      setDisableCreateOrder={setDisableCreateOrder}
                      valueTicket={valueTicket}
                      onChangeTicket={onChangeTicket}
                      isShowTag={isShowTag}
                      isCreatingOrderInProxyBuyerFlow={
                        isCreatingOrderInProxyBuyerFlow
                      }
                    />
                  </Col>
                  <Col span={device === 'desktop' ? 8 : 24}>
                    {isFirstLoad || (
                      <PaymentInfo
                        disableCreateOrder={disableCreateOrder}
                        allocatedDebt={allocatedDebt}
                        deliverTicketTypeList={deliverTicketTypeList}
                        isCreatingOrderInProxyBuyerFlow={
                          isCreatingOrderInProxyBuyerFlow
                        }
                      />
                    )}
                  </Col>
                </Row>
              ) : null}
            </Col>
          </ContainerFluid>
        </LoadingContainer>
      </FormCustomContext.Provider>
      <ModalContainer
        isShow={!!promotionData}
        onCloseDialog={() => setPromotionData(null)}
        maskClosable={false}
        modalProps={{
          destroyOnClose: true,
          width: 'auto',
        }}
        modalPadding="32px 30px 24px"
        modalHeight="auto"
        CloseIcon={<CloseIcon />}
        styleSlots={{
          iconWrapperStyle: {
            margin: 0,
          },
          modalContentStyle: {
            width: 636,
            maxWidth: device === 'desktop' ? 'none' : '90vw',
          },
        }}
      >
        <PromotionDetail
          title={promotionData?.name}
          parkAddress={promotionData?.park?.address}
          description={promotionData?.description}
          note={promotionData?.note}
        />
      </ModalContainer>
    </>
  );
});
